/**
 * Own wording for the row labels of the Fondsdaten table (page 1) and the Gebühren table
 * (page 2).
 *
 * Weekly 02.10.2026: SQUAD sets the raised digits that point to a footnote themselves —
 * "Vertriebszulassung²", "Einstufung nach SFDR³" — or leaves them out. The labels used to
 * be fixed in the template, so they can now be overridden per document.
 *
 * Only overrides are stored (`additional_fields.fondsdatenLabels` / `weitereDatenLabels`):
 * a label left at the template wording follows the template if that wording ever changes.
 * An empty override is kept on purpose — clearing a label must stick.
 */

export type SquadLabelKey = { key: string; label: string };
export type SquadLabelOverrides = Record<string, string>;

/** Overrides for known keys only; anything that is not a string is dropped. */
export function normalizeSquadLabelOverrides(value: unknown, keys: SquadLabelKey[]): SquadLabelOverrides {
  const out: SquadLabelOverrides = {};
  if (!value || typeof value !== 'object') return out;
  const map = value as Record<string, unknown>;
  for (const { key, label } of keys) {
    const own = map[key];
    if (typeof own === 'string' && own !== label) out[key] = own;
  }
  return out;
}

/** The key list with the document's wording applied. */
export function squadLabeledKeys(keys: SquadLabelKey[], overrides: SquadLabelOverrides | undefined): SquadLabelKey[] {
  return keys.map(({ key, label }) => {
    const own = overrides?.[key];
    return { key, label: typeof own === 'string' ? own : label };
  });
}

/** Set one label; typing the template wording again removes the override. */
export function setSquadLabelOverride(
  overrides: SquadLabelOverrides | undefined,
  keys: SquadLabelKey[],
  key: string,
  next: string,
): SquadLabelOverrides {
  const out: SquadLabelOverrides = { ...(overrides || {}) };
  const templateLabel = keys.find((k) => k.key === key)?.label;
  if (next === templateLabel) delete out[key];
  else out[key] = next;
  return out;
}
